import { Injectable, inject } from '@angular/core';
import { PlatformChartGateActivationDto } from '@data/_generated-api/platform-api/models/platform-chart-gate-activation-dto';
import { PlatformChartListItemDto } from '@data/_generated-api/platform-api/models/platform-chart-list-item-dto';
import { PlatformChartType } from '@data/_generated-api/platform-api/models/platform-chart-type';
import { PlatformDataSourceQuery } from '@data/_generated-api/platform-api/models/platform-data-source-query';
import { PlatformFilterOption } from '@data/_generated-api/platform-api/models/platform-filter-option';
import { DsQueryFilterOperator } from '@common/result/data-source/enum/ds-query-filter-operator';
import { HdIncarnationCross, HdProfile } from '../bodygraph/basic-properties/hd-chart-properties.types';
import { HdLifeCross } from '../bodygraph/basic-properties/hd-chart-properties.util';
import { HdChartMatchScope } from '../bodygraph/chart-tabs/hd-chart-global-settings';
import { HdChartGroupService } from './hd-chart-group.service';
import { HdChartLibraryService } from './hd-chart-library.service';
import { HdChartGateMatchMode } from './hd-chart-library.models';

export type { HdChartMatchScope };

/** Welke gepromoveerde eigenschap gematcht wordt. */
export type HdChartMatchKind = 'profile' | 'incarnationCross';

export interface HdChartMatchRequest {
  readonly kind: HdChartMatchKind;
  /** Canonieke matchwaarde: profiel "1/3" of kruis-key "right-13-7-1-2". */
  readonly value: string;
  readonly scope: HdChartMatchScope;
  /** Bronchart voor de relatie-scope (bij een cycle chart: de natal chart). */
  readonly sourceChartId: string | null;
  /** Chart-ids die nooit in de lijst horen (de bronchart zelf, de actieve chart). */
  readonly excludeChartIds?: readonly string[];
}

/**
 * Canoniek "min-max"-poortpaar, bv. "10-15" — contract met de backend-extractor
 * (ChartDocumentMetadataExtractor.CanonicalGatePair), die de kolomwaarden in
 * hetzelfde formaat opslaat.
 */
export function hdCanonicalGatePairKey(a: number, b: number): string {
  return a <= b ? `${a}-${b}` : `${b}-${a}`;
}

/** De drie omgeving-zoektochten (matches fase B). */
export type HdChartEnvironmentSearchKind =
  /** Mijn kruis-paren (zon/aarde per kant) in andermans node-paren (beide kanten). */
  | 'crossToNodes'
  /** Mijn losse profiel-lijnen in andermans node-lijnen (beide kanten). */
  | 'profileToNodeLines'
  /** Mijn levenskruis (node-paren + node-lijnen) in andermans kruis-paren/profiel-lijnen. */
  | 'lifeCrossToCross';

/** Waar een omgeving-hit gevonden is (voor de plek-indicator in de lijst). */
export type HdChartEnvironmentMatchLocation =
  | 'nodesPersonality'
  | 'nodesDesign'
  | 'crossPersonality'
  | 'crossDesign'
  | 'profilePersonality'
  | 'profileDesign';

export interface HdChartEnvironmentMatchRequest {
  readonly kind: HdChartEnvironmentSearchKind;
  readonly scope: HdChartMatchScope;
  readonly sourceChartId: string | null;
  readonly excludeChartIds?: readonly string[];
  /** Bron-eigenschappen van de actieve chart; alleen het veld van de `kind` is vereist. */
  readonly incarnationCross?: HdIncarnationCross | null;
  readonly profile?: HdProfile | null;
  readonly lifeCross?: HdLifeCross | null;
}

/** Eén treffer: de plek plus de matchende waarde (lijn "6" of poortpaar "25-46"). */
export interface HdChartEnvironmentMatchHit {
  readonly location: HdChartEnvironmentMatchLocation;
  readonly value: string;
}

export interface HdChartEnvironmentMatch {
  readonly item: PlatformChartListItemDto;
  /** Alle treffers van deze chart, in vaste zoektocht-volgorde. */
  readonly hits: readonly HdChartEnvironmentMatchHit[];
  /** Alle distincte bronwaarden van de zoektocht (lijnen/paren) komen in de hits voor. */
  readonly strong: boolean;
}

/** Eén deel-zoektocht: de filters + de plek en waarde die een treffer aanduiden. */
interface EnvironmentQueryPart {
  readonly location: HdChartEnvironmentMatchLocation;
  readonly value: string;
  readonly filters: readonly PlatformFilterOption[];
}

/** Poortzoektocht (spec 2026-10-21-poortactivatie-zoektochten): één of meer poorten, En of Of. */
export interface HdChartGateMatchRequest {
  readonly gates: readonly number[];
  readonly match: HdChartGateMatchMode;
  readonly scope: HdChartMatchScope;
  /** Bronchart voor de relatie-scope (bij een cycle chart: de natal chart). */
  readonly sourceChartId: string | null;
  /** Chart-ids die nooit in de lijst horen (de actieve chart, zijn bron). */
  readonly excludeChartIds?: readonly string[];
}

/** Treffer van een poortzoektocht: de chart en zijn activaties op de gezochte poorten. */
export interface HdChartGateMatch {
  readonly item: PlatformChartListItemDto;
  /** Activaties van de treffer op de gezochte poorten (planeet, zijde, lijn), in poortvolgorde. */
  readonly activations: readonly PlatformChartGateActivationDto[];
}

/** Backend-kolom per match-kind (Chart-entity properties, zie hd-chart-datasource-query). */
const MATCH_PROPERTY_BY_KIND: Readonly<Record<HdChartMatchKind, string>> = {
  profile: 'HdProfile',
  incarnationCross: 'HdIncarnationCrossKey',
};

/** Backend-contract: pageSize 1..100 — matchlijsten kappen we op één volle pagina. */
/**
 * Zoekt charts uit de eigen bibliotheek die dezelfde gepromoveerde eigenschap
 * dragen (profiel of incarnatiekruis) via het bestaande datasource-endpoint.
 * Alleen Standard-charts tellen mee (cycle/just-now-charts zijn geen personen).
 * De `related`-scope snijdt client-side met de relatie-links van de bronchart
 * (bestaand relations-endpoint) — geen backend-wijziging nodig.
 */
@Injectable({ providedIn: 'root' })
export class HdChartMatchesService {
  private readonly library = inject(HdChartLibraryService);
  private readonly groups = inject(HdChartGroupService);

  async findMatches(request: HdChartMatchRequest): Promise<PlatformChartListItemDto[]> {
    if (!request.value) {
      return [];
    }
    const items = await this.library.queryAllCharts(this.buildStandardQuery([
      {
        propertyName: MATCH_PROPERTY_BY_KIND[request.kind],
        operator: DsQueryFilterOperator.StringEquals,
        value: request.value,
      },
    ]));

    const exclude = new Set(request.excludeChartIds ?? []);
    const matches = items.filter(item => item.id != null && !exclude.has(item.id));
    return this.applyRelatedScope(matches, request.scope, request.sourceChartId);
  }

  /**
   * De omgeving-zoektochten. Bewust deel-matches, nooit alles-tegelijk: elke
   * losse lijn of elk los poortpaar wordt in béíde kanten van de doelkolommen
   * gezocht (een volledige match zou nauwelijks hits geven). Treffers worden
   * per chart samengevoegd mét de matchende waarde; dekt een chart álle
   * distincte bronwaarden, dan is de match `strong`.
   */
  async findEnvironmentMatches(request: HdChartEnvironmentMatchRequest): Promise<HdChartEnvironmentMatch[]> {
    const parts = this.environmentQueryParts(request);
    if (parts.length === 0) {
      return [];
    }

    const results = await Promise.all(parts.map(async part => ({
      part,
      items: await this.library.queryAllCharts(this.buildStandardQuery(part.filters)),
    })));

    const exclude = new Set(request.excludeChartIds ?? []);
    const byId = new Map<string, { item: PlatformChartListItemDto; hits: HdChartEnvironmentMatchHit[] }>();
    for (const result of results) {
      for (const item of result.items) {
        if (item.id == null || exclude.has(item.id)) {
          continue;
        }
        const entry = byId.get(item.id) ?? { item, hits: [] };
        if (!entry.hits.some(hit => hit.location === result.part.location && hit.value === result.part.value)) {
          entry.hits.push({ location: result.part.location, value: result.part.value });
        }
        byId.set(item.id, entry);
      }
    }

    const scopedItems = await this.applyRelatedScope(
      [...byId.values()].map(entry => entry.item),
      request.scope,
      request.sourceChartId,
    );
    const scopedIds = new Set(scopedItems.map(item => item.id));

    const sourceValues = [...new Set(parts.map(part => part.value))];
    return [...byId.values()]
      .filter(entry => scopedIds.has(entry.item.id))
      .map(entry => ({
        item: entry.item,
        hits: entry.hits,
        strong: sourceValues.every(value => entry.hits.some(hit => hit.value === value)),
      }))
      .sort((a, b) => (a.item.name ?? '').localeCompare(b.item.name ?? ''));
  }

  /**
   * Charts die de gevraagde poorten activeren (server-side poortfilter, En of
   * Of). Bewust zonder typefilter: een cycle chart telt met zijn cyclusmoment
   * mee (cycli die iets doen op de poorten van de actieve chart), een composite
   * heeft geen doorzoekbare activaties en valt vanzelf weg.
   */
  async findGateMatches(request: HdChartGateMatchRequest): Promise<HdChartGateMatch[]> {
    const gates = [...new Set(request.gates)].filter(gate => Number.isInteger(gate) && gate >= 1 && gate <= 64);
    if (gates.length === 0) {
      return [];
    }
    const items = await this.library.queryAllCharts(
      this.buildQuery([]),
      undefined,
      { gates, match: request.match },
    );

    const exclude = new Set(request.excludeChartIds ?? []);
    const scoped = await this.applyRelatedScope(
      items.filter(item => item.id != null && !exclude.has(item.id)),
      request.scope,
      request.sourceChartId,
    );
    const gateSet = new Set(gates);
    return scoped.map(item => ({
      item,
      activations: (item.hdGateActivations ?? [])
        .filter(activation => activation.gate != null && gateSet.has(activation.gate)),
    }));
  }

  /** Vertaalt de bron-eigenschappen naar de deel-queries van de gevraagde zoektocht. */
  private environmentQueryParts(request: HdChartEnvironmentMatchRequest): EnvironmentQueryPart[] {
    const stringEquals = (propertyName: string, value: string): PlatformFilterOption =>
      ({ propertyName, operator: DsQueryFilterOperator.StringEquals, value });
    const intEquals = (propertyName: string, value: number): PlatformFilterOption =>
      ({ propertyName, operator: DsQueryFilterOperator.IntEquals, value: `${value}` });

    switch (request.kind) {
      case 'crossToNodes': {
        // Elk kruis-paar apart, in beide node-kanten (zon/aarde delen per kant
        // hun paar; een 11/12-paar in andermans D-nodes telt dus ook).
        const gates = request.incarnationCross?.gates;
        if (gates == null || gates.length !== 4) return [];
        const pairs = [...new Set([
          hdCanonicalGatePairKey(gates[0], gates[1]),
          hdCanonicalGatePairKey(gates[2], gates[3]),
        ])];
        return pairs.flatMap(pair => [
          { location: 'nodesPersonality' as const, value: pair, filters: [stringEquals('HdNodePairPersonality', pair)] },
          { location: 'nodesDesign' as const, value: pair, filters: [stringEquals('HdNodePairDesign', pair)] },
        ]);
      }
      case 'profileToNodeLines': {
        // Elke profiel-lijn apart, in beide node-kanten (6/2 → een 6 óf een 2
        // in hun P- of D-nodes is al een hit; beide lijnen samen = strong).
        const profile = request.profile;
        if (profile == null) return [];
        const lines = [...new Set([profile.personalityLine, profile.designLine])];
        return lines.flatMap(line => [
          { location: 'nodesPersonality' as const, value: `${line}`, filters: [intEquals('HdNodeLinePersonality', line)] },
          { location: 'nodesDesign' as const, value: `${line}`, filters: [intEquals('HdNodeLineDesign', line)] },
        ]);
      }
      case 'lifeCrossToCross': {
        // Omgekeerd, zelfde stramien: elk node-paar in beide kruis-kanten en
        // elke node-lijn in beide losse profiel-lijnen van de ander.
        const lifeCross = request.lifeCross;
        if (lifeCross == null) return [];
        const pairs = [...new Set([
          hdCanonicalGatePairKey(lifeCross.personality.southGate, lifeCross.personality.northGate),
          hdCanonicalGatePairKey(lifeCross.design.southGate, lifeCross.design.northGate),
        ])];
        const parts: EnvironmentQueryPart[] = pairs.flatMap(pair => [
          { location: 'crossPersonality' as const, value: pair, filters: [stringEquals('HdCrossPairPersonality', pair)] },
          { location: 'crossDesign' as const, value: pair, filters: [stringEquals('HdCrossPairDesign', pair)] },
        ]);
        // De knopen delen per kant de lijn; wijkt dat af (corrupt document),
        // dan doet die kant niet mee.
        const lines = [...new Set([
          lifeCross.personality.southLine === lifeCross.personality.northLine ? lifeCross.personality.southLine : null,
          lifeCross.design.southLine === lifeCross.design.northLine ? lifeCross.design.southLine : null,
        ])].filter((line): line is number => line != null);
        parts.push(...lines.flatMap(line => [
          { location: 'profilePersonality' as const, value: `${line}`, filters: [intEquals('HdProfileLinePersonality', line)] },
          { location: 'profileDesign' as const, value: `${line}`, filters: [intEquals('HdProfileLineDesign', line)] },
        ]));
        return parts;
      }
    }
  }

  /** Standard-charts-query met naam-sortering; cycle/just-now-charts zijn geen personen. */
  private buildStandardQuery(filters: readonly PlatformFilterOption[]): PlatformDataSourceQuery {
    return this.buildQuery([
      {
        propertyName: 'Type',
        operator: DsQueryFilterOperator.IntEquals,
        value: `${PlatformChartType.Standard}`,
      },
      ...filters,
    ]);
  }

  /** Query met naam-sortering; zonder filters geen filtergroep (backend-contract: minstens één entry). */
  private buildQuery(filters: readonly PlatformFilterOption[]): PlatformDataSourceQuery {
    return {
      sortOptions: [{ propertyName: 'Name', direction: 'ASC' }],
      filterOptionGroup: filters.length
        ? { logicalOperator: '', filterOptionGroups: [], filterOptions: [...filters] }
        : null,
    };
  }

  private async applyRelatedScope(
    matches: PlatformChartListItemDto[],
    scope: HdChartMatchScope,
    sourceChartId: string | null,
  ): Promise<PlatformChartListItemDto[]> {
    if (scope !== 'related' || !sourceChartId) {
      return matches;
    }
    const relations = await this.groups.listRelations(sourceChartId);
    const relatedIds = new Set(
      relations
        .map(relation => relation.otherChartId)
        .filter((id): id is string => id != null),
    );
    return matches.filter(item => relatedIds.has(item.id!));
  }
}
